import React, { useEffect, useState, useMemo } from 'react';
import { FileUploader } from '../components/FileUploader';
import { MenuImportReview } from './MenuImportReview';
import { apiFetch } from '../api/client';
import { resolveAssetUrl } from '../utils/url';

const DOC_TYPES = [
    { value: 'MENU_COMPLETO', label: 'Menu Completo' },
    { value: 'MENU_BAR', label: 'Menu Bar' },
    { value: 'MENU_RISTORANTE', label: 'Menu Ristorante' },
    { value: 'MENU_CAFFETTERIA', label: 'Menu Caffetteria' },
    { value: 'MENU_COLAZIONE', label: 'Menu Colazione' },
    { value: 'MENU_PRANZO', label: 'Menu Pranzo' },
    { value: 'MENU_GELATO', label: 'Menu Gelato' },
    { value: 'MENU_TAGLIERE', label: 'Menu Tagliere' },
    { value: 'MENU_FESTE', label: 'Menu Feste e Eventi' },
    { value: 'ALLERGENI', label: 'Allergeni' }
];

type UploadedFile = {
    id?: number;
    url: string;
    downloadUrl: string;
    name: string;
    type: string;
    title?: string;
};

const mapBackendDocument = (doc: any): UploadedFile => {
    const rawUrl = doc?.url || '';
    const type = doc?.docType || doc?.type || 'MENU_COMPLETO';
    const title = doc?.title || doc?.name || rawUrl.split('/').pop() || type;
    const normalized = resolveAssetUrl(rawUrl);
    return {
        id: doc?.id,
        url: rawUrl,
        downloadUrl: normalized || rawUrl,
        name: title,
        type,
        title
    };
};

const createLocalDocument = (url: string, name: string, type: string): UploadedFile => {
    const normalized = resolveAssetUrl(url);
    return {
        url,
        downloadUrl: normalized || url,
        name,
        type
    };
};

export function DocumentUpload() {
    const [docType, setDocType] = useState('MENU_COMPLETO');
    const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
    const [selectedFile, setSelectedFile] = useState<UploadedFile | null>(null);
    const tenantId = useMemo(() => {
        const stored = localStorage.getItem('tenantId');
        return stored && stored.trim() !== '' ? stored : '1';
    }, []);

    // Load existing documents from backend
    useEffect(() => {
        (async () => {
            try {
                const res = await apiFetch(`/api/public/tenants/${tenantId}/documents`);
                if (!res.ok) return;
                const docs = await res.json();
                setUploadedFiles(docs.map((d: any) => mapBackendDocument(d)));
            } catch { /* ignore */ }
        })();
    }, [tenantId]);

    const handleUpload = async (url: string, name: string) => {
        // Persist metadata as document
        try {
            const res = await apiFetch('/api/tenant/self/documents', {
                method: 'POST',
                body: JSON.stringify({ type: docType, url, title: name }),
                headers: { 'Content-Type': 'application/json' }
            });
            if (res.ok) {
                const doc = await res.json();
                setUploadedFiles(prev => [...prev, mapBackendDocument(doc)]);
            } else {
                setUploadedFiles(prev => [...prev, createLocalDocument(url, name, docType)]);
            }
        } catch {
            setUploadedFiles(prev => [...prev, createLocalDocument(url, name, docType)]);
        }
    };

    const handleDelete = async (file: UploadedFile) => {
        if (file?.id) {
            try { await apiFetch(`/api/tenant/self/documents/${file.id}`, { method: 'DELETE' }); } catch { /* ignore */ }
        }
        setUploadedFiles(prev => prev.filter(f => file.id ? f.id !== file.id : f.url !== file.url));
    };

    return (
        <div style={{ maxWidth: 520, margin: '2.5rem auto', background: '#fff', borderRadius: '1.5rem', boxShadow: '0 4px 16px rgba(67,97,238,0.08)', padding: '2.5rem 2rem', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <h2 style={{ marginBottom: '2rem', color: 'var(--accent)', fontWeight: 700, fontSize: '2rem', letterSpacing: '0.5px' }}>Upload Documenti/Menu</h2>
            <div style={{ width: '100%', marginBottom: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.7rem' }}>
                <label style={{ fontWeight: 600, color: 'var(--secondary)', fontSize: '1.1rem' }}>Tipo documento</label>
                <select value={docType} onChange={e => setDocType(e.target.value)} style={{ padding: '0.7rem 1rem', borderRadius: '0.7rem', border: '1px solid #e0e0e0', fontSize: '1.05rem', background: '#f7f8fc', fontWeight: 500 }}>
                    {DOC_TYPES.map(dt => <option key={dt.value} value={dt.value}>{dt.label}</option>)}
                </select>
            </div>
            <div style={{ width: '100%', marginBottom: '2rem', background: '#f7f8fc', borderRadius: '1rem', boxShadow: '0 2px 8px rgba(67,97,238,0.04)', padding: '1.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <FileUploader label="Carica file (PDF, JPG, PNG)" onUpload={url => handleUpload(url, url.split('/').pop() || 'file')} />
            </div>
            <div style={{ width: '100%' }}>
                <h4 style={{ marginBottom: '1rem', color: 'var(--secondary)', fontWeight: 600, fontSize: '1.15rem' }}>File caricati</h4>
                {uploadedFiles.length === 0 ? <span style={{ color: '#888', fontSize: '1.05rem' }}>Nessun file caricato.</span> : (
                    <ul style={{ listStyle: 'none', padding: 0 }}>
                        {uploadedFiles.map(f => {
                            const label = DOC_TYPES.find(dt => dt.value === f.type)?.label || f.type;
                            const key = f.id ? `${f.id}` : f.url;
                            return (
                                <li key={key} style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.7rem', background: '#f7f8fc', borderRadius: '0.7rem', padding: '0.7rem 1rem', fontSize: '1.05rem' }}>
                                    <span style={{ fontWeight: 500, cursor: 'pointer' }} onClick={() => setSelectedFile(f)}>{f.name}</span>
                                    <span style={{ color: '#888', fontSize: '0.98rem' }}>{label}</span>
                                    <a href={f.downloadUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)', textDecoration: 'underline', marginLeft: 'auto', fontWeight: 500 }}>Apri</a>
                                    <button onClick={() => setSelectedFile(f)} style={{ marginLeft: '.5rem', background: '#43a047', color: '#fff', border: 'none', padding: '.35rem .7rem', borderRadius: '.5rem', cursor: 'pointer' }}>Review</button>
                                    <button onClick={() => handleDelete(f)} style={{ background: 'transparent', border: 'none', color: '#e57373', cursor: 'pointer', fontSize: '1.2rem' }} title="Elimina file">🗑️</button>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>

            {selectedFile && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
                    <div style={{ background: '#fff', borderRadius: '1rem', boxShadow: '0 8px 40px rgba(0,0,0,0.25)', maxWidth: '90%', maxHeight: '90%', overflow: 'auto' }}>
                        <MenuImportReview tenantId={tenantId} fileUrl={selectedFile.downloadUrl} onClose={() => setSelectedFile(null)} />
                    </div>
                </div>
            )}
        </div>
    );
}
